import '@fontsource-variable/archivo/wght.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster } from 'sonner'
import { AppRouter } from './app/router'
import { ApiRequestError } from './lib/api'
import { LocaleProvider } from './lib/i18n'
import { registerServiceWorker } from './lib/push'
import { ThemeProvider } from './lib/theme'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Retrying a 4xx just delays the error the user needs to see. Network and
      // 5xx failures are worth one more go on a flaky mobile connection.
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status < 500) return false
        return failureCount < 2
      },
    },
    mutations: { retry: false },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('#root missing')

// Precached shell and push handling. Failure is silent — the app works without it.
registerServiceWorker()

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <LocaleProvider>
        <QueryClientProvider client={queryClient}>
          <AppRouter />
          <Toaster
            position="top-center"
            closeButton
            // Inherit the app's tokens rather than sonner's own palette, so toasts
            // follow the theme switch without a second source of truth.
            toastOptions={{
              classNames: {
                toast:
                  'rounded-card! border-border! bg-surface! text-ink! shadow-pop! font-sans! text-sm!',
                description: 'text-ink-muted!',
                actionButton: 'bg-primary! text-on-primary!',
                closeButton: 'bg-surface! border-border! text-ink-muted!',
                // Colour the status icon, not the message — the text stays readable.
                success: '[&_[data-icon]]:text-success',
                error: '[&_[data-icon]]:text-danger',
                warning: '[&_[data-icon]]:text-warning',
              },
            }}
          />
        </QueryClientProvider>
      </LocaleProvider>
    </ThemeProvider>
  </StrictMode>,
)
